import {
	TextField,
	Grid,
	Box,
	Typography,
	Button,
	ButtonGroup,
	MenuItem,
	Chip,
	Alert,
} from '@mui/material'
import {
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { alertModal, confirmAlert, isEmpty } from '../../../util/util'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { AskSearchType, AskType } from 'constants/appType'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiAskDetail, apiAskList, apiAskSave } from 'api/api'
import { SpRequestType } from 'constants/types'
import dayjs from 'dayjs'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import React from 'react'
import { extractCmmnCode } from 'util/cmmnCodeUtil'
import { selectCmmnCode } from 'hooks/cmmnCodeFactory'
import { useSelector } from 'react-redux'
import { replaceEnterToBr } from 'util/stringUtil'
import AskPopup from './AskPopup'
import { selectuserInfo } from 'hooks/userInfo'

// React Query 키 팩토리
const askKeys = {
	all: ['ask'] as const,
	lists: () => [...askKeys.all, 'list'] as const,
	list: (params: AskSearchType) => [...askKeys.lists(), params] as const,
}

const Ask = () => {
	const userInfo = useSelector(selectuserInfo)
	const cmmdCd = useSelector(selectCmmnCode)
	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const disabledAdd = true
	const [disabledDel, setDisabledDel] = useState(true)
	const [disabledSave, setDisabledSave] = useState(true)
	const disabledSearch = false
	const disabledReset = false
	const disabledExcelDownload = false
	const hideAdd = true
	const hideDel = true
	const hideSave = true
	const hideSearch = false
	const hideReset = false
	const hideExcelDownload = true
	const [loading, setLoading] = useState(false)

	// 필터 상태: 'all' | 'unanswered' | 'answered'
	const [filterType, setFilterType] = useState<
		'all' | 'unanswered' | 'answered'
	>('all')

	const queryClient = useQueryClient()

	const gridColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'id',
				headerName: 'id',
				width: 90,
			},
			{
				field: 'crud',
				headerName: 'crud',
				width: 90,
			},
			{
				field: 'userId',
				headerName: '사용자 ID',
				width: 150,
			},
			{
				field: 'type',
				headerName: '문의 유형',
				width: 150,
			},
			{
				field: 'requestMessage',
				headerName: '문의 내용',
				minWidth: 300,
				flex: 1,
				renderCell: (params) => {
					const { type, answerMessage, title } = params.row
					return (
						<Box sx={{ display: 'flex' }}>
							{!isEmpty(answerMessage) ? (
								<Chip
									label="답변완료"
									color="success"
									size="small"
									sx={{ mr: 1 }}
								/>
							) : (
								<Chip
									label="미답변"
									color="warning"
									size="small"
									sx={{ mr: 1 }}
								/>
							)}
							<Chip
								label={type}
								size="small"
								color="info"
								sx={{ mr: 1 }}
							/>
							<Typography>{title}</Typography>
						</Box>
					)
				},
			},
			{
				field: 'answerMessage',
				headerName: '답변 내용',
				width: 100,
				renderCell: (params) => {
					const { value } = params

					if (value) {
						return (
							<Button
								variant="outlined"
								size="small"
								color="primary"
								onClick={() => getDetail(params.row.id)}
							>
								상세보기
							</Button>
						)
					} else {
						return (
							<Button
								variant="contained"
								size="small"
								color="info"
								onClick={() => getDetail(params.row.id)}
							>
								답변하기
							</Button>
						)
					}
				},
			},
			{
				field: 'name',
				headerName: '작성자',
				width: 250,
				renderCell: (params) => {
					const { name, phone } = params.row
					return (
						<span style={{ display: 'flex', gap: '20px' }}>
							<span>{name}</span>
							<span>{phone}</span>
						</span>
					)
				},
			},
			{
				field: 'createdAt',
				headerName: '작성일',
				type: 'string',
				width: 180,
				renderCell: (params) => {
					// createdAt보다 updatedAt값의 날짜 차이를 계산해서 upatedAt이 크면 updatedAt을 표시한다 또한 날짜가 오늘이면 시분초만 표기하고 아니면 년월일 시분초 표기
					const createdAt = dayjs(params.row.createdAt)
					const updatedAt = dayjs(params.row.updatedAt)
					const isToday = updatedAt.isSame(dayjs(), 'day')

					if (updatedAt.isAfter(createdAt)) {
						return isToday
							? updatedAt.format('HH:mm:ss')
							: updatedAt.format('YYYY-MM-DD HH:mm:ss')
					} else {
						return isToday
							? createdAt.format('HH:mm:ss')
							: createdAt.format('YYYY-MM-DD HH:mm:ss')
					}
				},
			},
			{
				field: 'updatedAt',
				headerName: '수정일',
				type: 'string',
				width: 180,
				renderCell: (params) => {
					const updatedAt = dayjs(params.row.updatedAt)
					const isToday = updatedAt.isSame(dayjs(), 'day')

					return isToday
						? updatedAt.format('HH:mm:ss')
						: updatedAt.format('YYYY-MM-DD HH:mm:ss')
				},
			},
		],
		[],
	)

	const [search, setSearch] = useState<AskSearchType>({
		id: 0,
		userId: 0,
		regId: '',
		modId: '',
		msg: '',
		startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		endDate: dayjs().format('YYYY-MM-DD'),
		type: 'all',
		keyword: '',
	})

	// React Query를 사용한 게시판 목록 조회
	const {
		data: askData = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: askKeys.list(search),
		queryFn: async () => {
			setLoading(true)
			console.log('apiBoardList 호출 시작 - 검색 파라미터:', search)
			const request: AskSearchType = {
				...search,
				type: search.type === 'all' ? '' : search.type,
			}
			const result = await apiAskList(request).finally(() => setLoading(false))
			console.log('apiBoardList 호출 성공 - 결과:', result)
			return result
		},
		staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
		gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지

		// 자동 리페칭 옵션들
		refetchOnWindowFocus: true, // 윈도우 포커스 시 자동 리페칭
		refetchOnReconnect: true, // 네트워크 재연결 시 자동 리페칭
		refetchOnMount: true, // 컴포넌트 마운트 시 자동 리페칭

		// 주기적 리페칭
		refetchInterval: 10 * 60 * 1000, // 30분마다 자동 리페칭
		refetchIntervalInBackground: false, // 백그라운드에서는 리페칭 안함
	})

	// 추가
	const actionAdd = () => {}

	// 삭제
	const actionDel = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('삭제할 문의를 선택해주세요.', 'warning', () => {})
			return
		}

		const selectedRows: AskType[] = selectedIds.map((id) => {
			return apiRef.current!.getRow(id) as AskType
		})

		// 저장 로직 구현 (api 호출 등)
		console.log('삭제할 문의:', selectedRows)

		// selectedRows에서 crud값을 D로 변경
		const rowsToUpdate = selectedRows.map((row) => ({
			...row,
			crud: 'D',
		}))

		if (rowsToUpdate.length === 0) {
			alertModal('삭제할 변경된 문의가 없습니다.', 'info', () => {})
			return
		}

		if (rowsToUpdate.length > 0) {
			confirmAlert(
				'선택한 문의를 삭제하시겠습니까?',
				() => {
					const request: SpRequestType = {
						cmd: 'DELETE',
						jsonText: JSON.stringify(rowsToUpdate),
					}

					setDisabledSave(true)
					apiAskSave(request)
						.then((res) => {
							console.log('문의 저장 결과:', res)
							alertModal('문의가 성공적으로 저장되었습니다.', 'success', () => {
								// 저장 후 목록 다시 조회
								queryClient.invalidateQueries({
									queryKey: askKeys.lists(),
								})
							})
						})
						.finally(() => {
							setDisabledSave(false)
						})
				},
				() => {},
			)
		}
	}

	// 저장
	const actionSave = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('저장할 문의를 선택해주세요.', 'warning', () => {})
			return
		}

		const selectedRows: AskType[] = selectedIds.map((id) => {
			return apiRef.current!.getRow(id) as AskType
		})

		// 저장 로직 구현 (api 호출 등)
		console.log('저장할 문의:', selectedRows)

		// selectedRows에서 crud값이 U 인것만 선택
		const rowsToUpdate = selectedRows.filter((row) => row.crud === 'U')

		if (rowsToUpdate.length === 0) {
			alertModal('저장할 변경된 문의가 없습니다.', 'info', () => {})
			return
		}

		if (rowsToUpdate.length > 0) {
			confirmAlert(
				'선택한 문의를 저장하시겠습니까?',
				() => {
					const request: SpRequestType = {
						cmd: 'UPDATE',
						jsonText: JSON.stringify(rowsToUpdate),
					}

					setDisabledSave(true)
					apiAskSave(request)
						.then((res) => {
							console.log('문의 저장 결과:', res)
							alertModal('문의가 성공적으로 저장되었습니다.', 'success', () => {
								// 저장 후 목록 다시 조회
								queryClient.invalidateQueries({
									queryKey: askKeys.lists(),
								})
							})
						})
						.finally(() => {
							setDisabledSave(false)
						})
				},
				() => {},
			)
		}
	}

	// 검색 (React Query refetch 사용)
	const actionSearch = useCallback(() => {
		refetch()
	}, [refetch])

	// 초기화
	const actionReset = useCallback(() => {
		const initialSearch: AskSearchType = {
			id: 0,
			userId: 0,
			regId: '',
			modId: '',
			msg: '',
			startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
			endDate: dayjs().format('YYYY-MM-DD'),
			type: 'all',
			keyword: '',
		}
		setSearch(initialSearch)
		setRowSelectionModel({ type: 'include', ids: new Set() })

		// 그리드 상태 초기화
		if (apiRef.current) {
			apiRef.current.setPage(0)
			apiRef.current.setFilterModel({
				items: [],
			})
			apiRef.current.setSortModel([])
			apiRef.current.setColumnVisibilityModel({})
		}

		// 캐시 무효화 및 새로운 데이터 가져오기
		queryClient.invalidateQueries({
			queryKey: askKeys.lists(),
		})
	}, [queryClient, apiRef])

	// 키보드 엔터키 처리
	const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter' || e.key === 'Enter') {
			e.preventDefault() // 폼 제출 방지
			actionSearch()
		}
	}

	// 폼 제출 방지
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	useEffect(() => {
		const list = extractCmmnCode(cmmdCd, 'ASK_TYPE', 1) || []
		console.log(list.children)
		actionSearch()
	}, [])

	// 선택된 행에 따른 삭제 버튼 활성화/비활성화
	useEffect(() => {
		setDisabledDel(rowSelectionModel.ids.size === 0)
		setDisabledSave(rowSelectionModel.ids.size === 0)
	}, [rowSelectionModel.ids.size])

	// 검색 파라미터 변경 시 자동 검색 (debounce 적용)
	// search 상태가 변경되면 queryKey가 변경되어 자동으로 리페칭됨
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			// search 상태가 변경되면 React Query가 자동으로 새로운 쿼리를 실행
		}, 500) // 500ms debounce

		return () => clearTimeout(timeoutId)
	}, [search.id, search.userId, search.regId, search.modId])

	const [askDetail, setAskDetail] = useState<AskType>({
		crud: '',
		id: 0,
		userId: 0,
		requestMessage: '',
		answerMessage: '',
		regId: '',
		modId: '',
		createdAt: '',
		updatedAt: '',
		type: '',
		name: '',
		phone: '',
		title: '',
	})

	const [popupIsOpen, setPopupIsOpen] = useState(false)

	const getDetail = (id: number) => {
		console.log('상세보기 또는 답변하기 - ID:', id)
		if (id) {
			apiAskDetail(id).then((res) => {
				console.log('1:1 문의 상세 조회 결과:', res)
				setAskDetail(res)
				setPopupIsOpen(true)
			})
		}
	}

	const onPopupClose = () => {
		setPopupIsOpen(false)
		setAskDetail({
			crud: '',
			id: 0,
			userId: 0,
			requestMessage: '',
			answerMessage: '',
			regId: '',
			modId: '',
			createdAt: '',
			updatedAt: '',
			type: '',
			title: '',
		})
	}

	const popupSave = () => {
		console.log(askDetail)
		// askDetail 검증
		if (isEmpty(askDetail.answerMessage)) {
			alertModal('답변 내용을 입력해주세요.', 'warning', () => {})
			return
		}

		confirmAlert(
			'문의 답변을 저장하시겠습니까?',
			() => {
				const request: SpRequestType = {
					cmd: askDetail.id === 0 ? 'SAVE' : 'UPDATE',
					jsonText: JSON.stringify([
						{
							...askDetail,
							crud: askDetail.id === 0 ? 'C' : 'U',
						},
					]),
				}

				setDisabledSave(true)
				apiAskSave(request)
					.then((res) => {
						console.log('문의 저장 결과:', res)
						alertModal('문의가 성공적으로 저장되었습니다.', 'success', () => {
							// 저장 후 목록 다시 조회
							queryClient.invalidateQueries({
								queryKey: askKeys.lists(),
							})
							onPopupClose()
						})
					})
					.finally(() => {
						setDisabledSave(false)
					})
			},
			() => {},
		)
	}

	return (
		<PageContainer>
			<AskPopup
				popupIsOpen={popupIsOpen}
				onPopupClose={onPopupClose}
				askDetail={askDetail}
				setAskDetail={setAskDetail}
				popupSave={popupSave}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer
					onKeyDown={onKeyDown}
					onSubmit={onSubmit}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid size={4}>
							<LocalizationProvider
								dateAdapter={AdapterMoment}
								adapterLocale="ko"
							>
								<Box
									sx={{
										display: 'flex',
										gap: 2,
										alignItems: 'center',
										width: '100%',
									}}
								>
									<DatePicker
										label="시작일"
										value={moment(search.startDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, startDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
									<Typography>~</Typography>
									<DatePicker
										label="종료일"
										value={moment(search.endDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, endDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
								</Box>
							</LocalizationProvider>
						</Grid>
						<Grid size={4}>
							<TextField
								label="이름, 내용 검색"
								variant="outlined"
								fullWidth
								size="small"
							/>
						</Grid>
						<Grid size={4}>
							<TextField
								label="문의 유형"
								variant="outlined"
								fullWidth
								size="small"
								select
							>
								<MenuItem value="all">전체</MenuItem>
								<MenuItem value="title">서비스</MenuItem>
								<MenuItem value="content">기술</MenuItem>
							</TextField>
						</Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				<Box sx={{ height: 'calc(100% - 35px)' }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<ButtonGroup sx={{ mb: 1 }}>
							<Button
								variant={filterType === 'all' ? 'contained' : 'outlined'}
								color="primary"
								onClick={() => setFilterType('all')}
							>
								전체 ({askData?.length || 0})
							</Button>
							<Button
								variant={filterType === 'unanswered' ? 'contained' : 'outlined'}
								color="warning"
								onClick={() => setFilterType('unanswered')}
							>
								미답변 (
								{askData?.filter((row: AskType) => isEmpty(row.answerMessage)).length ||
									0}
								)
							</Button>
							<Button
								variant={filterType === 'answered' ? 'contained' : 'outlined'}
								color="success"
								onClick={() => setFilterType('answered')}
							>
								답변완료 (
								{askData?.filter((row: AskType) => !isEmpty(row.answerMessage))
									.length || 0}
								)
							</Button>
						</ButtonGroup>
						{isError && (
							<Box sx={{ mb: 2 }}>
								<Alert severity="error">
									데이터를 불러오는 중 오류가 발생했습니다: {error?.message}
								</Alert>
							</Box>
						)}
					</Box>
					<StyledDataGrid
						apiRef={apiRef}
						columns={gridColumns}
						rows={
							askData?.filter((row: AskType) => {
								if (filterType === 'all') return true
								if (filterType === 'unanswered') return isEmpty(row.answerMessage)
								if (filterType === 'answered') return !isEmpty(row.answerMessage)
								return true
							}) || []
						}
						showToolbar
						loading={isFetching || loading}
						localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
						getRowHeight={() => 'auto'}
						sx={{
							'& .MuiDataGrid-cell': {
								py: 1, // 셀 상하 패딩
								alignItems: 'flex-start',
							},
						}}
						initialState={{
							columns: {
								columnVisibilityModel: {
									id: false,
									userId: false,
									crud: false,
								},
							},
						}}
					/>
				</Box>
			</GridContainer>
		</PageContainer>
	)
}

export default Ask
